import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';
import {
  ErrorBanner,
  PROJECT_STATUSES,
  PROJECT_TYPES,
  SortTh,
  lower,
  sortRows,
  useGridSort,
} from './Configuration';

// Project-shaped admin settings: the methodology templates a schedule is built from, plus the
// Projects and Accounts grids that used to live on Resource Configuration (Jeremy, 2026-08-20).
//
// The grid helpers are imported from Configuration.js rather than copied -- five grids there already
// use them, and duplicating a sort comparator per page is how two grids start behaving differently.

// Mirrors PHASE_STAGES / WORKSTREAM_KINDS / DEPENDENCY_TYPES in packages/shared -- apps/web can't
// import it (ADR 0019), and these are too small to be worth a round trip.
const PHASE_STAGES = [
  { value: 'pre-project', label: 'Pre-project' },
  { value: 'core', label: 'Core delivery' },
  { value: 'post-project', label: 'Post-project' },
];

const WORKSTREAM_KINDS = [
  { value: 'delivery', label: 'Delivery' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'ongoing', label: 'Ongoing' },
];

const DEPENDENCY_TYPES = [
  { value: 'finish-to-start', label: 'FS' },
  { value: 'start-to-start', label: 'SS' },
  { value: 'finish-to-finish', label: 'FF' },
  { value: 'start-to-finish', label: 'SF' },
];

/**
 * One phase's template rows: each task followed by its own activities, then any activity whose
 * parent sits in another phase — those would otherwise vanish from every group on the page.
 */
export function groupTaskRows(tasks, phaseId) {
  const own = tasks.filter((t) => (t.phaseId ?? null) === (phaseId ?? null));
  const parents = own.filter((t) => !t.parentTaskId);
  return [
    ...parents.flatMap((parent) => [parent, ...own.filter((t) => t.parentTaskId === parent.id)]),
    ...own.filter((t) => t.parentTaskId && !parents.some((p) => p.id === t.parentTaskId)),
  ];
}

/** '' back to null so clearing a field means "no value", not the string "". */
const orNull = (value) => (value === '' || value === undefined ? null : value);
const numOrNull = (value) => (value === '' || value === null || value === undefined ? null : Number(value));

// ---------- Project templates ----------

export function TemplatesTab() {
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  // One draft map for every grid on this tab -- row ids are unique across phases/workstreams/tasks,
  // and `kind` on each draft says which endpoint the row saves to.
  const [drafts, setDrafts] = useState({});
  const [newPhase, setNewPhase] = useState('');
  const [newWorkstream, setNewWorkstream] = useState('');
  const [newTaskFor, setNewTaskFor] = useState({});

  const loadList = useCallback(async () => {
    try {
      const rows = await apiFetch('/project-templates?includeInactive=true');
      setTemplates(rows);
      setSelectedId((prev) => prev || rows.find((t) => t.isDefault)?.id || rows[0]?.id || '');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Deliberately does NOT clear `drafts` or `error`: it runs after every write, including a partly
  // failed save, where the rows that failed must keep both their edits and the message saying why.
  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      setDetail(await apiFetch(`/project-templates/${id}`));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);
  // Switching template is the one point drafts are abandoned — they belong to rows that are no
  // longer on screen.
  useEffect(() => { setDrafts({}); }, [selectedId]);

  const refresh = useCallback(async () => {
    await Promise.all([loadList(), loadDetail(selectedId)]);
  }, [loadList, loadDetail, selectedId]);

  /** Runs a write, then reloads — every mutation here changes counts the list shows. */
  async function run(label, fn) {
    setBusy(label);
    setError('');
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  }

  const fieldValue = (kind, row, field) => {
    const draft = drafts[row.id];
    if (draft && draft[field] !== undefined) return draft[field];
    const value = row[field];
    return value === null || value === undefined ? (typeof value === 'boolean' ? value : '') : value;
  };

  const setField = (kind, id, field, value) => {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], kind, [field]: value } }));
  };

  const dirtyCount = Object.keys(drafts).length;

  // Each dirty row saves independently and in parallel, so one rejected row (a duplicate title, say)
  // leaves the rest saved and only that row still dirty for a retry -- the People-grid convention.
  async function saveAll() {
    const entries = Object.entries(drafts);
    if (!entries.length) return;
    setBusy('save');
    setError('');
    const failures = [];
    const results = await Promise.all(
      entries.map(async ([id, draft]) => {
        const { kind, ...fields } = draft;
        try {
          await apiFetch(`/project-templates/${kind}/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(bodyFor(kind, fields)),
          });
          return id;
        } catch (err) {
          failures.push(err.message);
          return null;
        }
      }),
    );
    const saved = new Set(results.filter(Boolean));
    setDrafts((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => !saved.has(id))));
    // Reload before reporting: the loaders can surface their own error, and this message is the one
    // the user needs to see.
    await refresh();
    setError(failures.length ? failures.join(' · ') : '');
    setBusy('');
  }

  function bodyFor(kind, fields) {
    if (kind === 'phases') {
      return {
        ...(fields.title !== undefined ? { title: fields.title } : {}),
        ...(fields.sequence !== undefined ? { sequence: numOrNull(fields.sequence) ?? 1 } : {}),
        ...(fields.stage !== undefined ? { stage: fields.stage } : {}),
        ...(fields.methodologyName !== undefined ? { methodologyName: orNull(fields.methodologyName) } : {}),
        ...(fields.conditional !== undefined ? { conditional: fields.conditional } : {}),
        ...(fields.conditionNote !== undefined ? { conditionNote: orNull(fields.conditionNote) } : {}),
        ...(fields.durationWeight !== undefined ? { durationWeight: numOrNull(fields.durationWeight) } : {}),
      };
    }
    if (kind === 'workstreams') {
      return {
        ...(fields.name !== undefined ? { name: fields.name } : {}),
        ...(fields.kind !== undefined ? { kind: fields.kind } : {}),
        ...(fields.sortOrder !== undefined ? { sortOrder: numOrNull(fields.sortOrder) } : {}),
      };
    }
    if (kind === 'dependencies') {
      return {
        ...(fields.type !== undefined ? { type: fields.type } : {}),
        ...(fields.lagDays !== undefined ? { lagDays: numOrNull(fields.lagDays) ?? 0 } : {}),
      };
    }
    return {
      ...(fields.title !== undefined ? { title: fields.title } : {}),
      ...(fields.phaseId !== undefined ? { phaseId: orNull(fields.phaseId) } : {}),
      ...(fields.workstreamId !== undefined ? { workstreamId: orNull(fields.workstreamId) } : {}),
      ...(fields.durationDays !== undefined ? { durationDays: numOrNull(fields.durationDays) } : {}),
      ...(fields.effortHours !== undefined ? { effortHours: numOrNull(fields.effortHours) } : {}),
      ...(fields.defaultRole !== undefined ? { defaultRole: orNull(fields.defaultRole) } : {}),
      ...(fields.isMilestone !== undefined ? { isMilestone: fields.isMilestone } : {}),
    };
  }

  const selected = templates.find((t) => t.id === selectedId);

  // Phase order is the schedule's order, so this grid is deliberately not sortable -- `sequence` is
  // the column you edit to reorder it.
  const phases = detail?.phases ?? [];
  const workstreams = detail?.workstreams ?? [];
  // Memoized because the `?? []` fallback is a new array each render, which would re-run every
  // downstream useMemo.
  const tasks = useMemo(() => detail?.tasks ?? [], [detail]);
  const dependencies = useMemo(() => detail?.dependencies ?? [], [detail]);

  const taskById = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const predecessorsOf = useMemo(() => {
    const map = new Map();
    dependencies.forEach((d) => {
      map.set(d.successorId, [...(map.get(d.successorId) ?? []), d]);
    });
    return map;
  }, [dependencies]);

  if (loading) return <div className="card">Loading…</div>;

  return (
    <div>
      <ErrorBanner error={error} />

      {!templates.length ? (
        <div className="card">
          <div className="card-title">No project templates yet</div>
          <p style={{ fontSize: 13, color: 'var(--text-mid)', maxWidth: 640 }}>
            Seeding creates “Aumentum Standard Methodology” from the delivery methodology already in
            the code — eight phases, their objectives as tasks and their deliverables as milestones.
            Edit it freely afterward; re-seeding only adds what’s missing.
          </p>
          <button className="btn btn-primary" disabled={busy === 'seed'}
            onClick={() => run('seed', () => apiFetch('/project-templates/seed', { method: 'POST' }))}>
            {busy === 'seed' ? 'Seeding…' : 'Seed standard methodology'}
          </button>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 280 }}>
                <label className="form-label">Template</label>
                <select className="form-select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.isDefault ? ' (default)' : ''}
                      {t.isActive ? '' : ' — inactive'}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <button className="btn btn-outline" disabled={!selected || selected.isDefault || busy}
                  onClick={() => run('default', () => apiFetch(`/project-templates/${selectedId}/set-default`, { method: 'POST' }))}>
                  Make default
                </button>
                <button className="btn btn-outline" disabled={!selected || busy}
                  onClick={() => {
                    const name = window.prompt('Name for the copy', `${selected.name} (copy)`);
                    if (name) run('clone', async () => {
                      const copy = await apiFetch(`/project-templates/${selectedId}/clone`, {
                        method: 'POST', body: JSON.stringify({ name }),
                      });
                      setSelectedId(copy.id);
                    });
                  }}>
                  Duplicate
                </button>
                <button className="btn btn-outline" disabled={!selected || busy}
                  onClick={() => run('active', () => apiFetch(`/project-templates/${selectedId}`, {
                    method: 'PATCH', body: JSON.stringify({ isActive: !selected.isActive }),
                  }))}>
                  {selected?.isActive ? 'Deactivate' : 'Reactivate'}
                </button>
                <button className="btn btn-outline" disabled={busy}
                  onClick={() => {
                    const name = window.prompt('New template name');
                    if (name) run('new', async () => {
                      const created = await apiFetch('/project-templates', {
                        method: 'POST', body: JSON.stringify({ name }),
                      });
                      setSelectedId(created.id);
                    });
                  }}>
                  New
                </button>
                <button className="btn btn-outline" disabled={!selected || busy}
                  onClick={() => {
                    if (window.confirm(`Delete "${selected.name}" and everything in it?`)) {
                      run('delete', async () => {
                        await apiFetch(`/project-templates/${selectedId}`, { method: 'DELETE' });
                        setSelectedId('');
                      });
                    }
                  }}>
                  Delete
                </button>
              </div>
              {selected && (
                <div style={{ fontSize: 12, color: 'var(--text-mid)', marginLeft: 'auto' }}>
                  {selected.phaseCount} phases · {selected.workstreamCount} workstreams ·{' '}
                  {selected.taskCount} tasks · used by {selected.projectCount} project(s)
                </div>
              )}
            </div>
          </div>

          {dirtyCount > 0 && (
            <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="btn btn-primary" disabled={busy === 'save'} onClick={saveAll}>
                {busy === 'save' ? 'Saving…' : `Save changes (${dirtyCount})`}
              </button>
              <button className="btn btn-outline" disabled={busy === 'save'} onClick={() => setDrafts({})}>
                Discard
              </button>
            </div>
          )}

          {/* ---- Phases ---- */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">Phases</div>
            <div className="sheet-wrap">
              <table className="sheet">
                <thead>
                  <tr>
                    <th style={{ width: 60, textAlign: 'center' }}>Seq</th>
                    <th style={{ minWidth: 220 }}>Title</th>
                    <th style={{ width: 150 }}>Stage</th>
                    <th style={{ width: 180 }}>Methodology name</th>
                    <th style={{ width: 90, textAlign: 'center' }} title="Only projects that need it get this phase">
                      Optional
                    </th>
                    <th style={{ width: 110, textAlign: 'center' }} title="Share of the project duration relative to the other phases">
                      Weight
                    </th>
                    <th style={{ width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {phases.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <input className="form-input" type="number" min="1" style={{ width: 54, textAlign: 'center' }}
                          value={fieldValue('phases', p, 'sequence')}
                          onChange={(e) => setField('phases', p.id, 'sequence', e.target.value)} />
                      </td>
                      <td>
                        <input className="form-input" value={fieldValue('phases', p, 'title')}
                          onChange={(e) => setField('phases', p.id, 'title', e.target.value)} />
                      </td>
                      <td>
                        <select className="form-select" value={fieldValue('phases', p, 'stage')}
                          onChange={(e) => setField('phases', p.id, 'stage', e.target.value)}>
                          {PHASE_STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <input className="form-input" value={fieldValue('phases', p, 'methodologyName')}
                          onChange={(e) => setField('phases', p.id, 'methodologyName', e.target.value)} />
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={Boolean(fieldValue('phases', p, 'conditional'))}
                          onChange={(e) => setField('phases', p.id, 'conditional', e.target.checked)} />
                      </td>
                      <td>
                        <input className="form-input" type="number" min="1" style={{ width: 80, textAlign: 'center' }}
                          value={fieldValue('phases', p, 'durationWeight')}
                          onChange={(e) => setField('phases', p.id, 'durationWeight', e.target.value)} />
                      </td>
                      <td>
                        <button className="btn btn-outline" disabled={busy}
                          onClick={() => run('phase-del', () => apiFetch(`/project-templates/phases/${p.id}`, { method: 'DELETE' }))}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td />
                    <td colSpan={6}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="form-input" placeholder="New phase title" value={newPhase}
                          onChange={(e) => setNewPhase(e.target.value)} />
                        <button className="btn btn-outline" disabled={!newPhase.trim() || busy}
                          onClick={() => run('phase-add', async () => {
                            await apiFetch(`/project-templates/${selectedId}/phases`, {
                              method: 'POST', body: JSON.stringify({ title: newPhase.trim() }),
                            });
                            setNewPhase('');
                          })}>
                          Add phase
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- Workstreams ---- */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title">Workstreams</div>
            <p style={{ fontSize: 12, color: 'var(--text-mid)', marginTop: -8 }}>
              Streams of work that run across phases. A task can belong to one.
            </p>
            <div className="sheet-wrap is-fit">
              <table className="sheet">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Name</th>
                    <th style={{ width: 150 }}>Kind</th>
                    <th style={{ width: 90, textAlign: 'center' }}>Order</th>
                    <th style={{ width: 70 }} />
                  </tr>
                </thead>
                <tbody>
                  {workstreams.map((w) => (
                    <tr key={w.id}>
                      <td>
                        <input className="form-input" value={fieldValue('workstreams', w, 'name')}
                          onChange={(e) => setField('workstreams', w.id, 'name', e.target.value)} />
                      </td>
                      <td>
                        <select className="form-select" value={fieldValue('workstreams', w, 'kind')}
                          onChange={(e) => setField('workstreams', w.id, 'kind', e.target.value)}>
                          {WORKSTREAM_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                        </select>
                      </td>
                      <td>
                        <input className="form-input" type="number" min="0" style={{ width: 70, textAlign: 'center' }}
                          value={fieldValue('workstreams', w, 'sortOrder')}
                          onChange={(e) => setField('workstreams', w.id, 'sortOrder', e.target.value)} />
                      </td>
                      <td>
                        <button className="btn btn-outline" disabled={busy}
                          onClick={() => run('ws-del', () => apiFetch(`/project-templates/workstreams/${w.id}`, { method: 'DELETE' }))}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td colSpan={4}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="form-input" placeholder="New workstream name" value={newWorkstream}
                          onChange={(e) => setNewWorkstream(e.target.value)} />
                        <button className="btn btn-outline" disabled={!newWorkstream.trim() || busy}
                          onClick={() => run('ws-add', async () => {
                            await apiFetch(`/project-templates/${selectedId}/workstreams`, {
                              method: 'POST', body: JSON.stringify({ name: newWorkstream.trim() }),
                            });
                            setNewWorkstream('');
                          })}>
                          Add workstream
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ---- Tasks & activities, grouped by phase ---- */}
          {[...phases, { id: null, title: 'Not in a phase' }].map((phase) => (
            <div className="card" style={{ marginBottom: 16 }} key={phase.id ?? 'none'}>
              <div className="card-title">{phase.title}</div>
              <TaskGrid
                rows={groupTaskRows(tasks, phase.id)}
                phaseId={phase.id}
                templateId={selectedId}
                workstreams={workstreams}
                tasks={tasks}
                taskById={taskById}
                predecessorsOf={predecessorsOf}
                fieldValue={fieldValue}
                setField={setField}
                busy={busy}
                run={run}
                newTaskFor={newTaskFor}
                setNewTaskFor={setNewTaskFor}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * One phase's tasks, with each task's activities indented beneath it and its predecessor links
 * inline. Links are written immediately rather than batched with the field drafts: an edge is a
 * relation, not a value, and a half-saved graph is worse than a slower click.
 */
function TaskGrid({
  rows, phaseId, templateId, workstreams, tasks, taskById, predecessorsOf,
  fieldValue, setField, busy, run, newTaskFor, setNewTaskFor,
}) {
  const key = phaseId ?? 'none';
  const draftTitle = newTaskFor[key] ?? '';
  const [linkFor, setLinkFor] = useState('');

  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>
            <th style={{ minWidth: 260 }}>Task / activity</th>
            <th style={{ width: 170 }}>Workstream</th>
            <th style={{ width: 90, textAlign: 'center' }} title="Working days — sizes the bar inside the phase">Days</th>
            <th style={{ width: 90, textAlign: 'center' }} title="Estimated effort in hours">Hours</th>
            <th style={{ width: 130 }}>Default role</th>
            <th style={{ width: 80, textAlign: 'center' }}>Milestone</th>
            <th style={{ minWidth: 220 }}>Depends on</th>
            <th style={{ width: 150 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const isActivity = Boolean(t.parentTaskId);
            const links = predecessorsOf.get(t.id) ?? [];
            return (
              <tr key={t.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, paddingLeft: isActivity ? 22 : 0 }}>
                    {isActivity && <span style={{ color: 'var(--text-mid)' }}>↳</span>}
                    <input className="form-input" value={fieldValue('tasks', t, 'title')}
                      onChange={(e) => setField('tasks', t.id, 'title', e.target.value)} />
                  </div>
                </td>
                <td>
                  <select className="form-select" value={fieldValue('tasks', t, 'workstreamId') || ''}
                    onChange={(e) => setField('tasks', t.id, 'workstreamId', e.target.value)}>
                    <option value="">—</option>
                    {workstreams.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </td>
                <td>
                  <input className="form-input" type="number" min="1" style={{ width: 70, textAlign: 'center' }}
                    value={fieldValue('tasks', t, 'durationDays')}
                    onChange={(e) => setField('tasks', t.id, 'durationDays', e.target.value)} />
                </td>
                <td>
                  <input className="form-input" type="number" min="0" style={{ width: 70, textAlign: 'center' }}
                    value={fieldValue('tasks', t, 'effortHours')}
                    onChange={(e) => setField('tasks', t.id, 'effortHours', e.target.value)} />
                </td>
                <td>
                  <input className="form-input" value={fieldValue('tasks', t, 'defaultRole')}
                    onChange={(e) => setField('tasks', t.id, 'defaultRole', e.target.value)} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input type="checkbox" checked={Boolean(fieldValue('tasks', t, 'isMilestone'))}
                    onChange={(e) => setField('tasks', t.id, 'isMilestone', e.target.checked)} />
                </td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    {links.map((d) => (
                      <span key={d.id} className="badge badge-blue" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {taskById.get(d.predecessorId)?.title ?? 'unknown'}
                        {/* Link type and lag are edited in place and saved with everything else. */}
                        <select
                          className="form-select" style={{ fontSize: 11, padding: '0 4px', width: 52 }}
                          value={fieldValue('dependencies', d, 'type')}
                          onChange={(e) => setField('dependencies', d.id, 'type', e.target.value)}
                        >
                          {DEPENDENCY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <input
                          className="form-input" type="number" title="Lag in days (negative for a lead)"
                          style={{ fontSize: 11, padding: '0 4px', width: 46, textAlign: 'center' }}
                          value={fieldValue('dependencies', d, 'lagDays')}
                          onChange={(e) => setField('dependencies', d.id, 'lagDays', e.target.value)}
                        />
                        <button
                          aria-label="Remove dependency"
                          style={{ border: 'none', background: 'none', cursor: 'pointer', marginLeft: 4 }}
                          disabled={busy}
                          onClick={() => run('dep-del', () => apiFetch(`/project-templates/dependencies/${d.id}`, { method: 'DELETE' }))}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    {linkFor === t.id ? (
                      <select className="form-select" autoFocus defaultValue=""
                        onChange={(e) => {
                          const predecessorId = e.target.value;
                          setLinkFor('');
                          if (predecessorId) {
                            run('dep-add', () => apiFetch(`/project-templates/${templateId}/dependencies`, {
                              method: 'POST', body: JSON.stringify({ predecessorId, successorId: t.id }),
                            }));
                          }
                        }}>
                        <option value="">Choose a predecessor…</option>
                        {tasks
                          .filter((o) => o.id !== t.id && !links.some((d) => d.predecessorId === o.id))
                          .map((o) => <option key={o.id} value={o.id}>{o.title}</option>)}
                      </select>
                    ) : (
                      <button className="btn btn-outline" disabled={busy} onClick={() => setLinkFor(t.id)}>+ link</button>
                    )}
                  </div>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {!isActivity && (
                      <button className="btn btn-outline" disabled={busy}
                        onClick={() => {
                          const title = window.prompt(`New activity under "${t.title}"`);
                          if (title) run('act-add', () => apiFetch(`/project-templates/${templateId}/tasks`, {
                            method: 'POST',
                            body: JSON.stringify({ title, phaseId, parentTaskId: t.id, workstreamId: t.workstreamId }),
                          }));
                        }}>
                        + activity
                      </button>
                    )}
                    <button className="btn btn-outline" disabled={busy}
                      onClick={() => run('task-del', () => apiFetch(`/project-templates/tasks/${t.id}`, { method: 'DELETE' }))}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          <tr>
            <td colSpan={8}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" placeholder="New task title" value={draftTitle}
                  onChange={(e) => setNewTaskFor((prev) => ({ ...prev, [key]: e.target.value }))} />
                <button className="btn btn-outline" disabled={!draftTitle.trim() || busy}
                  onClick={() => run('task-add', async () => {
                    await apiFetch(`/project-templates/${templateId}/tasks`, {
                      method: 'POST', body: JSON.stringify({ title: draftTitle.trim(), phaseId }),
                    });
                    setNewTaskFor((prev) => ({ ...prev, [key]: '' }));
                  })}>
                  Add task
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ---------- Which template a project uses ----------

/**
 * The template picker on the Projects grid, kept separate from the grid's own draft/save cycle:
 * repointing a project at another methodology only affects the *next* promotion, so it's a single
 * immediate write rather than something to batch with name and PM edits.
 */
function ProjectTemplatePicker({ project, templates, onChanged }) {
  const [saving, setSaving] = useState(false);
  return (
    <select
      className="form-select"
      disabled={saving || !templates.length}
      value={project.projectTemplateId ?? ''}
      onChange={async (e) => {
        setSaving(true);
        try {
          await apiFetch(`/projects/${project.id}`, {
            method: 'PATCH',
            body: JSON.stringify({ projectTemplateId: e.target.value || null }),
          });
          await onChanged();
        } finally {
          setSaving(false);
        }
      }}
    >
      <option value="">Default template</option>
      {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
}

// ---------- Projects (PM assignment + placeholders) ----------

// A placeholder can only be merged into a real, not-yet-merged project -- merging into another
// placeholder would just move the problem, not solve it (ProjectsService.mergePlaceholder rejects
// this server-side too; this just keeps the dropdown from offering it in the first place).
// Sentinel option value: a `<select>` can't hold a real link, so picking this entry opens the
// Accounts modal instead of assigning an account.
const MANAGE_ACCOUNTS_OPTION = '__manage_accounts__';

function mergeTargetOptions(projects, placeholder) {
  return projects.filter((p) => p.id !== placeholder.id && !p.isPlaceholder);
}

// Popped out into a modal (Jeremy, 2026-08-06) -- account renaming is rare, and the always-visible
// table was pushing the far more frequently used Projects table below the fold.
function AccountsModal({ accounts, accountFieldValue, setAccountField, accountDrafts, savingAccountId, onSave, onCreate, onClose }) {
  const [newAccount, setNewAccount] = useState({ name: '', state: '' });
  const [creating, setCreating] = useState(false);
  const [sort, toggleSort] = useGridSort();
  const sortedAccounts = useMemo(() => sortRows(accounts, sort, (account, key) => lower(account[key])), [accounts, sort]);

  async function submitNewAccount(e) {
    e.preventDefault();
    if (!newAccount.name.trim() || creating) return;
    setCreating(true);
    const created = await onCreate({
      name: newAccount.name.trim(),
      state: newAccount.state.trim() || undefined,
    });
    setCreating(false);
    if (created) setNewAccount({ name: '', state: '' });
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">Accounts</span>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="sheet-wrap is-fit">
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} style={{ minWidth: 240 }} />
                  <SortTh label="State" sortKey="state" sort={sort} onSort={toggleSort} style={{ width: 80 }} />
                  <th style={{ width: 100 }} />
                </tr>
              </thead>
              <tbody>
                {sortedAccounts.map((account) => {
                  const dirty = !!accountDrafts[account.id];
                  return (
                    <tr key={account.id} className={dirty ? 'is-dirty' : ''}>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={accountFieldValue(account, 'name')}
                          onChange={(e) => setAccountField(account.id, 'name', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          maxLength={50}
                          value={accountFieldValue(account, 'state')}
                          onChange={(e) => setAccountField(account.id, 'state', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {dirty && (
                          <button
                            className="btn btn-primary"
                            style={{ fontSize: 11, padding: '4px 10px' }}
                            disabled={savingAccountId === account.id}
                            onClick={() => onSave(account)}
                          >
                            {savingAccountId === account.id ? 'Saving…' : 'Save'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <form
            onSubmit={submitNewAccount}
            style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}
          >
            <div className="form-group" style={{ marginBottom: 0, flex: 1, minWidth: 200 }}>
              <label className="form-label">New account</label>
              <input
                type="text"
                className="form-input"
                placeholder="e.g. Ada County"
                maxLength={200}
                value={newAccount.name}
                onChange={(e) => setNewAccount((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0, width: 90 }}>
              <label className="form-label">State</label>
              <input
                type="text"
                className="form-input"
                placeholder="ID"
                maxLength={50}
                value={newAccount.state}
                onChange={(e) => setNewAccount((f) => ({ ...f, state: e.target.value }))}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={creating || !newAccount.name.trim()}>
              {creating ? 'Adding…' : '+ Add account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [mergeTarget, setMergeTarget] = useState({});
  const [mergingId, setMergingId] = useState('');
  const [confirmingMergeId, setConfirmingMergeId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState('');
  const [newPlaceholder, setNewPlaceholder] = useState({ name: '', accountId: '' });
  const [adding, setAdding] = useState(false);
  const [accountDrafts, setAccountDrafts] = useState({});
  const [savingAccountId, setSavingAccountId] = useState('');
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [sort, toggleSort] = useGridSort();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [projectData, accountData, userData, templateData] = await Promise.all([
        apiFetch('/projects'),
        apiFetch('/accounts'),
        apiFetch('/users'),
        apiFetch('/project-templates'),
      ]);
      setProjects(projectData);
      setAccounts(accountData);
      setUsers(userData);
      setTemplates(templateData);
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function fieldValue(project, field, defaultValue = '') {
    const draft = drafts[project.id];
    if (draft && draft[field] !== undefined) return draft[field];
    if (field === 'projectManagerId') return project.projectManagerId ?? defaultValue;
    return project[field] ?? defaultValue;
  }

  function setField(projectId, field, value) {
    setDrafts((prev) => ({ ...prev, [projectId]: { ...prev[projectId], [field]: value } }));
  }

  const accountNameById = useMemo(() => new Map(accounts.map((a) => [a.id, a.name])), [accounts]);
  const userNameById = useMemo(() => new Map(users.map((u) => [u.id, u.name])), [users]);
  const templateNameById = useMemo(() => new Map(templates.map((t) => [t.id, t.name])), [templates]);

  const sortedProjects = useMemo(
    () => sortRows(projects, sort, (project, key) => {
      switch (key) {
        case 'accountId': return lower(accountNameById.get(project.accountId));
        case 'projectManagerId': return lower(userNameById.get(project.projectManagerId));
        // Blank ("Default template") sorts with the templates, by the name shown, not by the id.
        case 'projectTemplateId': return lower(templateNameById.get(project.projectTemplateId));
        // Real projects first, then placeholders still to merge, then already-merged ones.
        case 'placeholder': return !project.isPlaceholder ? 0 : project.mergedIntoProjectId ? 2 : 1;
        default: return lower(project[key]);
      }
    }),
    [projects, sort, accountNameById, userNameById, templateNameById],
  );

  function patchProject(project) {
    return apiFetch(`/projects/${project.id}`, {
      method: 'PATCH',
      // `null`, not `undefined` -- see patchUser's comment; same JSON.stringify pitfall.
      body: JSON.stringify({
        name: fieldValue(project, 'name') || undefined,
        accountId: fieldValue(project, 'accountId') || undefined,
        projectManagerId: fieldValue(project, 'projectManagerId') || null,
        projectStatus: fieldValue(project, 'projectStatus') || undefined,
        projectType: fieldValue(project, 'projectType') || undefined,
      }),
    });
  }

  // One save for the whole grid (same convention as the People tab) instead of a button per row --
  // lets a mass edit pass (renaming several projects, reassigning statuses) happen in one go. Rows
  // save independently in parallel, so one bad edit doesn't block the rest.
  async function saveAll() {
    const dirtyIds = Object.keys(drafts);
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    setError('');
    const failedIds = new Set();
    const failureMessages = [];
    await Promise.all(
      dirtyIds.map(async (id) => {
        const project = projects.find((p) => p.id === id);
        if (!project) return;
        try {
          await patchProject(project);
        } catch (err) {
          failedIds.add(id);
          failureMessages.push(`${project.name}: ${err.message}`);
        }
      }),
    );
    try {
      setProjects(await apiFetch('/projects'));
    } catch (err) {
      failureMessages.push(err.message);
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of dirtyIds) if (!failedIds.has(id)) delete next[id];
      return next;
    });
    setError(failureMessages.length > 0 ? `${failureMessages.length} update(s) failed: ${failureMessages.join('; ')}` : '');
    setSavingAll(false);
  }

  function accountFieldValue(account, field) {
    const draft = accountDrafts[account.id];
    if (draft && draft[field] !== undefined) return draft[field];
    return account[field] ?? '';
  }

  function setAccountField(accountId, field, value) {
    setAccountDrafts((prev) => ({ ...prev, [accountId]: { ...prev[accountId], [field]: value } }));
  }

  async function saveAccount(account) {
    setSavingAccountId(account.id);
    setError('');
    try {
      await apiFetch(`/accounts/${account.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: accountFieldValue(account, 'name'),
          state: accountFieldValue(account, 'state'),
        }),
      });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingAccountId('');
    }
  }

  // Returns whether it landed, so the modal only clears its form on success.
  async function createAccount(payload) {
    setError('');
    try {
      await apiFetch('/accounts', { method: 'POST', body: JSON.stringify(payload) });
      await load();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }

  async function addPlaceholder(e) {
    e.preventDefault();
    if (!newPlaceholder.name.trim() || !newPlaceholder.accountId) return;
    setAdding(true);
    setError('');
    try {
      await apiFetch('/projects', {
        method: 'POST',
        body: JSON.stringify({
          name: newPlaceholder.name.trim(),
          accountId: newPlaceholder.accountId,
          isPlaceholder: true,
        }),
      });
      setNewPlaceholder({ name: '', accountId: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  async function mergePlaceholder(placeholder) {
    const intoProjectId = mergeTarget[placeholder.id];
    if (!intoProjectId) return;
    setMergingId(placeholder.id);
    setError('');
    try {
      await apiFetch(`/projects/${placeholder.id}/merge-placeholder`, {
        method: 'POST',
        body: JSON.stringify({ intoProjectId }),
      });
      setConfirmingMergeId('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setMergingId('');
    }
  }

  // The API refuses a project with logged hours (and the Administrative Time one) -- that message is
  // what lands in the error banner, so there's no client-side pre-check duplicating the rule here.
  async function deleteProject(project) {
    setDeletingId(project.id);
    setError('');
    try {
      await apiFetch(`/projects/${project.id}`, { method: 'DELETE' });
      setConfirmingDeleteId('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId('');
    }
  }

  const dirtyCount = Object.keys(drafts).length;

  return (
    <div>
      <ErrorBanner error={error} />
      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
              {dirtyCount > 0 ? `${dirtyCount} unsaved change${dirtyCount === 1 ? '' : 's'}` : ''}
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              {dirtyCount > 0 && (
                <button className="btn btn-primary" disabled={savingAll} onClick={saveAll}>
                  {savingAll ? 'Saving…' : 'Save changes'}
                </button>
              )}
              <button className="btn btn-outline" onClick={() => setAccountsModalOpen(true)}>
                Manage accounts
              </button>
            </div>
          </div>

          {accountsModalOpen && (
            <AccountsModal
              accounts={accounts}
              accountFieldValue={accountFieldValue}
              setAccountField={setAccountField}
              accountDrafts={accountDrafts}
              savingAccountId={savingAccountId}
              onSave={saveAccount}
              onCreate={createAccount}
              onClose={() => setAccountsModalOpen(false)}
            />
          )}

          <div className="sheet-wrap is-fit" style={{ marginBottom: 16 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Project" sortKey="name" sort={sort} onSort={toggleSort} style={{ minWidth: 220 }} />
                  <SortTh label="Account" sortKey="accountId" sort={sort} onSort={toggleSort} style={{ minWidth: 160 }} />
                  <SortTh label="Status" sortKey="projectStatus" sort={sort} onSort={toggleSort} style={{ minWidth: 140 }} />
                  <SortTh label="Project manager" sortKey="projectManagerId" sort={sort} onSort={toggleSort} style={{ minWidth: 180 }} />
                  <SortTh
                    label="Project type" sortKey="projectType" sort={sort} onSort={toggleSort}
                    style={{ minWidth: 130 }}
                    title="POC = percentage-of-completion (fixed fee), T&M = time and materials, Internal = non-billable, B&B = Book and Bill (fixed fee, like POC). T&M projects aren't offered in the Project Schedule picker."
                  />
                  <SortTh
                    label="Schedule template" sortKey="projectTemplateId" sort={sort} onSort={toggleSort}
                    style={{ minWidth: 170 }}
                    title="Methodology the next schedule promotion builds from"
                  />
                  <SortTh label="Placeholder" sortKey="placeholder" sort={sort} onSort={toggleSort} style={{ minWidth: 260 }} />
                  <th style={{ width: 150 }} />
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((project) => {
                  const dirty = drafts[project.id] !== undefined;
                  return (
                    <tr key={project.id} className={dirty ? 'is-dirty' : ''}>
                      <td style={{ padding: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            className="form-input"
                            style={{ border: 'none', background: 'transparent', flex: 1, minWidth: 0 }}
                            value={fieldValue(project, 'name')}
                            onChange={(e) => setField(project.id, 'name', e.target.value)}
                          />
                          {project.isPlaceholder && (
                            <span className="badge badge-yellow" style={{ flexShrink: 0 }}>Placeholder</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none' }}
                          value={fieldValue(project, 'accountId')}
                          onChange={(e) => {
                            if (e.target.value === MANAGE_ACCOUNTS_OPTION) {
                              setAccountsModalOpen(true);
                              return;
                            }
                            setField(project.id, 'accountId', e.target.value);
                          }}
                        >
                          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                          <option value={MANAGE_ACCOUNTS_OPTION}>— Manage accounts… —</option>
                        </select>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none' }}
                          value={fieldValue(project, 'projectStatus', 'Active')}
                          onChange={(e) => setField(project.id, 'projectStatus', e.target.value)}
                        >
                          {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none' }}
                          value={fieldValue(project, 'projectManagerId')}
                          onChange={(e) => setField(project.id, 'projectManagerId', e.target.value)}
                        >
                          <option value="">—</option>
                          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none' }}
                          value={fieldValue(project, 'projectType', 'POC')}
                          onChange={(e) => setField(project.id, 'projectType', e.target.value)}
                        >
                          {PROJECT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <ProjectTemplatePicker project={project} templates={templates} onChanged={load} />
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12, color: 'var(--text-mid)' }}>
                        {!project.isPlaceholder ? (
                          '—'
                        ) : project.mergedIntoProjectId ? (
                          `Merged into ${projects.find((p) => p.id === project.mergedIntoProjectId)?.name || '—'}`
                        ) : (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                              className="form-select"
                              style={{ fontSize: 12, padding: '4px 8px', width: 160 }}
                              value={mergeTarget[project.id] || ''}
                              onChange={(e) => setMergeTarget((prev) => ({ ...prev, [project.id]: e.target.value }))}
                            >
                              <option value="">Merge into…</option>
                              {mergeTargetOptions(projects, project).map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                            {confirmingMergeId === project.id ? (
                              <>
                                <button
                                  className="btn"
                                  style={{ background: 'var(--red)', color: 'var(--white)', fontSize: 11, padding: '4px 8px' }}
                                  disabled={mergingId === project.id}
                                  onClick={() => mergePlaceholder(project)}
                                >
                                  {mergingId === project.id ? 'Merging…' : 'Confirm merge'}
                                </button>
                                <button
                                  className="btn btn-outline"
                                  style={{ fontSize: 11, padding: '4px 8px' }}
                                  onClick={() => setConfirmingMergeId('')}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className="btn btn-outline"
                                style={{ fontSize: 11, padding: '4px 8px' }}
                                disabled={!mergeTarget[project.id]}
                                onClick={() => setConfirmingMergeId(project.id)}
                              >
                                Merge
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                        {project.isAdministrative ? null : confirmingDeleteId === project.id ? (
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button
                              className="btn"
                              style={{ background: 'var(--red)', color: 'var(--white)', fontSize: 11, padding: '4px 8px' }}
                              disabled={deletingId === project.id}
                              onClick={() => deleteProject(project)}
                            >
                              {deletingId === project.id ? 'Deleting…' : 'Confirm delete'}
                            </button>
                            <button
                              className="btn btn-outline"
                              style={{ fontSize: 11, padding: '4px 8px' }}
                              onClick={() => setConfirmingDeleteId('')}
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-outline"
                            style={{ fontSize: 11, padding: '4px 8px' }}
                            title="Deletes the project and its schedule, forecast, budget and BPA data. Refused if any time has been logged against it."
                            onClick={() => setConfirmingDeleteId(project.id)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-title">Add a placeholder project</div>
            <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 12 }}>
              For a project anticipated ahead of a signed SOW, so resourcing can start forecasting
              against it. Doesn't show up in Time Entry -- only real, live projects take logged
              hours. Merge it into the real project once the SOW is signed and it's created for
              real; its forecast moves over automatically.
            </div>
            <form onSubmit={addPlaceholder} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
                <label className="form-label">Project name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. ID-Ada-Phase 2 (anticipated)"
                  maxLength={300}
                  value={newPlaceholder.name}
                  onChange={(e) => setNewPlaceholder((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, minWidth: 200 }}>
                <label className="form-label">Account</label>
                <select
                  className="form-select"
                  value={newPlaceholder.accountId}
                  onChange={(e) => {
                    if (e.target.value === MANAGE_ACCOUNTS_OPTION) {
                      setAccountsModalOpen(true);
                      return;
                    }
                    setNewPlaceholder((f) => ({ ...f, accountId: e.target.value }));
                  }}
                >
                  <option value="">Select an account…</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  <option value={MANAGE_ACCOUNTS_OPTION}>— Manage accounts… —</option>
                </select>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={adding || !newPlaceholder.name.trim() || !newPlaceholder.accountId}
              >
                {adding ? 'Adding…' : '+ Add placeholder'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
// ---------- Modules ----------
// The BPA module taxonomy (Module/ProjectModule, ADR 0016) — CRUD already existed at /modules for
// BPA's own scoping, it just never had an admin screen. Same drafts/saveAll shape as Configuration.js's
// TaskTypesTab: retiring is isActive:false, not delete, since ModulesService.remove refuses a delete
// once anything (a child module, a BPA session, project scope) references the row.
export function ModulesTab() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [newModule, setNewModule] = useState({ name: '', code: '', parentModuleId: '' });
  const [adding, setAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [sort, toggleSort] = useGridSort();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setModules(await apiFetch(`/modules?includeInactive=${showInactive}`));
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  function fieldValue(module, field) {
    const draft = drafts[module.id];
    if (draft && draft[field] !== undefined) return draft[field];
    return module[field] ?? '';
  }

  function setField(id, field, value) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  const nameById = useMemo(() => new Map(modules.map((m) => [m.id, m.name])), [modules]);

  const sortedModules = useMemo(
    () => sortRows(modules, sort, (module, key) => {
      switch (key) {
        case 'parentModuleId': return lower(nameById.get(module.parentModuleId) || '');
        case 'sequence': return module.sequence ?? 99;
        case 'isActive': return module.isActive ? 1 : 0;
        default: return lower(module[key]);
      }
    }),
    [modules, sort, nameById],
  );

  function patchModule(module) {
    const parentModuleId = fieldValue(module, 'parentModuleId');
    return apiFetch(`/modules/${module.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: fieldValue(module, 'name'),
        code: fieldValue(module, 'code') || undefined,
        parentModuleId: parentModuleId || null,
        sequence: Number(fieldValue(module, 'sequence') || 99),
        isActive: fieldValue(module, 'isActive'),
      }),
    });
  }

  async function saveAll() {
    const dirtyIds = Object.keys(drafts);
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    setError('');
    const failedIds = new Set();
    const failureMessages = [];
    await Promise.all(
      dirtyIds.map(async (id) => {
        const module = modules.find((m) => m.id === id);
        if (!module) return;
        try {
          await patchModule(module);
        } catch (err) {
          failedIds.add(id);
          failureMessages.push(`${module.name}: ${err.message}`);
        }
      }),
    );
    try {
      setModules(await apiFetch(`/modules?includeInactive=${showInactive}`));
    } catch (err) {
      failureMessages.push(err.message);
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of dirtyIds) if (!failedIds.has(id)) delete next[id];
      return next;
    });
    setError(failureMessages.length > 0 ? `${failureMessages.length} update(s) failed: ${failureMessages.join('; ')}` : '');
    setSavingAll(false);
  }

  async function addModule(e) {
    e.preventDefault();
    if (!newModule.name.trim() || !newModule.code.trim()) return;
    setAdding(true);
    setError('');
    try {
      await apiFetch('/modules', {
        method: 'POST',
        body: JSON.stringify({
          name: newModule.name.trim(),
          code: newModule.code.trim(),
          parentModuleId: newModule.parentModuleId || undefined,
        }),
      });
      setNewModule({ name: '', code: '', parentModuleId: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  const dirtyCount = Object.keys(drafts).length;

  return (
    <div>
      {dirtyCount > 0 && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
          </div>
          <button className="btn btn-primary" disabled={savingAll} onClick={saveAll}>
            {savingAll ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
      <ErrorBanner error={error} />

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: 'var(--text-mid)' }}>
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
        Show inactive modules
      </label>

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div className="sheet-wrap is-fit" style={{ marginBottom: 16 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} style={{ width: 220 }} />
                  <SortTh label="Code" sortKey="code" sort={sort} onSort={toggleSort} style={{ width: 100 }} />
                  <SortTh label="Parent" sortKey="parentModuleId" sort={sort} onSort={toggleSort} style={{ width: 200 }} />
                  <SortTh label="Sequence" sortKey="sequence" sort={sort} onSort={toggleSort} style={{ width: 90 }} />
                  <SortTh label="Active" sortKey="isActive" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70 }} />
                </tr>
              </thead>
              <tbody>
                {sortedModules.map((module) => {
                  const dirty = !!drafts[module.id];
                  return (
                    <tr key={module.id} className={dirty ? 'is-dirty' : ''}>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(module, 'name')}
                          onChange={(e) => setField(module.id, 'name', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(module, 'code')}
                          onChange={(e) => setField(module.id, 'code', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <select
                          className="form-select"
                          style={{ border: 'none', width: '100%' }}
                          value={fieldValue(module, 'parentModuleId') || ''}
                          onChange={(e) => setField(module.id, 'parentModuleId', e.target.value)}
                        >
                          <option value="">— Top level —</option>
                          {modules.filter((m) => m.id !== module.id).map((m) => (
                            <option key={m.id} value={m.id}>{m.name}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          type="number"
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(module, 'sequence') || 99}
                          onChange={(e) => setField(module.id, 'sequence', e.target.value)}
                        />
                      </td>
                      <td className="cell-check" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!fieldValue(module, 'isActive')}
                          onChange={(e) => setField(module.id, 'isActive', e.target.checked)}
                        />
                      </td>
                    </tr>
                  );
                })}
                {modules.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No modules yet — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-title">Add a module</div>
            <form onSubmit={addModule} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 220 }}>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  maxLength={100}
                  value={newModule.name}
                  onChange={(e) => setNewModule((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 120 }}>
                <label className="form-label">Code</label>
                <input
                  type="text"
                  className="form-input"
                  maxLength={30}
                  value={newModule.code}
                  onChange={(e) => setNewModule((f) => ({ ...f, code: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 200 }}>
                <label className="form-label">Parent</label>
                <select
                  className="form-select"
                  value={newModule.parentModuleId}
                  onChange={(e) => setNewModule((f) => ({ ...f, parentModuleId: e.target.value }))}
                >
                  <option value="">— Top level —</option>
                  {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding}>
                {adding ? 'Adding…' : 'Add module'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Budget Categories ----------

// The fixed pick list Category on a Project Budgets line must come from (Jeremy, 2026-09-28) --
// replaces OnePlan's free-text cost categories, which were never the real source for this data.
// Same isActive/sequence convention as Modules above, minus the hierarchy -- this list is flat.
export function BudgetCategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [newCategory, setNewCategory] = useState({ name: '', sequence: '' });
  const [adding, setAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [sort, toggleSort] = useGridSort('sequence');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setCategories(await apiFetch(`/budget-categories${showInactive ? '?includeInactive=true' : ''}`));
      setDrafts({});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { load(); }, [load]);

  function fieldValue(category, field) {
    const draft = drafts[category.id];
    if (draft && draft[field] !== undefined) return draft[field];
    return category[field] ?? '';
  }

  function setField(id, field, value) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  }

  const sortedCategories = useMemo(
    () => sortRows(categories, sort, (category, key) => {
      switch (key) {
        case 'sequence': return category.sequence ?? 99;
        case 'isActive': return category.isActive ? 1 : 0;
        default: return lower(category.name);
      }
    }),
    [categories, sort],
  );

  function patchCategory(category) {
    return apiFetch(`/budget-categories/${category.id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: fieldValue(category, 'name'),
        sequence: Number(fieldValue(category, 'sequence') || 99),
        isActive: fieldValue(category, 'isActive'),
      }),
    });
  }

  async function saveAll() {
    const dirtyIds = Object.keys(drafts);
    if (dirtyIds.length === 0) return;
    setSavingAll(true);
    setError('');
    const failedIds = new Set();
    const failureMessages = [];
    await Promise.all(
      dirtyIds.map(async (id) => {
        const category = categories.find((c) => c.id === id);
        if (!category) return;
        try {
          await patchCategory(category);
        } catch (err) {
          failedIds.add(id);
          failureMessages.push(`${category.name}: ${err.message}`);
        }
      }),
    );
    try {
      await load();
    } catch (err) {
      failureMessages.push(err.message);
    }
    setDrafts((prev) => {
      const next = { ...prev };
      for (const id of dirtyIds) if (!failedIds.has(id)) delete next[id];
      return next;
    });
    setError(failureMessages.length > 0 ? `${failureMessages.length} update(s) failed: ${failureMessages.join('; ')}` : '');
    setSavingAll(false);
  }

  async function addCategory(e) {
    e.preventDefault();
    if (!newCategory.name.trim()) return;
    setAdding(true);
    setError('');
    try {
      await apiFetch('/budget-categories', {
        method: 'POST',
        body: JSON.stringify({
          name: newCategory.name.trim(),
          sequence: newCategory.sequence ? Number(newCategory.sequence) : undefined,
        }),
      });
      setNewCategory({ name: '', sequence: '' });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  const dirtyCount = Object.keys(drafts).length;

  return (
    <div>
      {dirtyCount > 0 && (
        <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            {dirtyCount} unsaved change{dirtyCount === 1 ? '' : 's'}
          </div>
          <button className="btn btn-primary" disabled={savingAll} onClick={saveAll}>
            {savingAll ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
      <ErrorBanner error={error} />

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: 'var(--text-mid)' }}>
        <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
        Show inactive categories
      </label>

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <div className="sheet-wrap is-fit" style={{ marginBottom: 16 }}>
            <table className="sheet">
              <thead>
                <tr>
                  <SortTh label="Name" sortKey="name" sort={sort} onSort={toggleSort} style={{ width: 420 }} />
                  <SortTh
                    label="Sequence" sortKey="sequence" sort={sort} onSort={toggleSort} style={{ width: 90 }}
                    title="Display order on the Category dropdown -- lower shows first, ties break alphabetically"
                  />
                  <SortTh
                    label="Active" sortKey="isActive" sort={sort} onSort={toggleSort} style={{ textAlign: 'center', width: 70 }}
                    title="Inactive categories stay on budget lines already using them, but are never offered for a new line"
                  />
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map((category) => {
                  const dirty = !!drafts[category.id];
                  return (
                    <tr key={category.id} className={dirty ? 'is-dirty' : ''}>
                      <td style={{ padding: '4px' }}>
                        <input
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(category, 'name')}
                          onChange={(e) => setField(category.id, 'name', e.target.value)}
                        />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <input
                          type="number"
                          className="form-input"
                          style={{ border: 'none', background: 'transparent' }}
                          value={fieldValue(category, 'sequence') || 99}
                          onChange={(e) => setField(category.id, 'sequence', e.target.value)}
                        />
                      </td>
                      <td className="cell-check" style={{ textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!fieldValue(category, 'isActive')}
                          onChange={(e) => setField(category.id, 'isActive', e.target.checked)}
                        />
                      </td>
                    </tr>
                  );
                })}
                {categories.length === 0 && (
                  <tr>
                    <td colSpan={3} style={{ padding: '16px', textAlign: 'center', color: 'var(--text-mid)', fontSize: 13 }}>
                      No budget categories yet — add one below.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="card-title">Add a budget category</div>
            <form onSubmit={addCategory} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ marginBottom: 0, width: 420 }}>
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  maxLength={200}
                  value={newCategory.name}
                  onChange={(e) => setNewCategory((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0, width: 100 }}>
                <label className="form-label">Sequence</label>
                <input
                  type="number"
                  className="form-input"
                  placeholder="99"
                  value={newCategory.sequence}
                  onChange={(e) => setNewCategory((f) => ({ ...f, sequence: e.target.value }))}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={adding || !newCategory.name.trim()}>
                {adding ? 'Adding…' : 'Add category'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

// Administration.js's tree nav renders each of the four Tab functions above directly (imported by
// name) rather than through a wrapper page component -- it owns the section/sub-tab labels and
// selection itself now, since the tree needs both levels' labels to draw the whole thing at once.
